/**
 * B2B Context Manager
 * 
 * Manages B2B/D2C context switching for the storefront.
 * - Handles localStorage persistence of context preference
 * - Toggles body class for CSS-based visibility
 * - Manages login redirects for non-authenticated users
 * - Free switching in Shopify design mode
 * 
 * localStorage key: 'jd_b2b_context'
 * - Key exists (any value) = B2B context active
 * - Key doesn't exist = Consumer/default context
 */

class B2BContextSwitcher extends HTMLElement {
  static STORAGE_KEY = 'jd_b2b_context';
  
  constructor() {
    super();
    this.toggle = this.querySelector('.b2b-switcher__toggle');
    this.dropdown = this.querySelector('.b2b-switcher__dropdown');
    this.options = this.querySelectorAll('.b2b-switcher__option');
  }

  connectedCallback() {
    this.initializeContext();
    this.bindEvents();
  }

  get config() {
    return window.FoxTheme?.b2b || {};
  }

  get isDesignMode() {
    return this.config.isDesignMode || Shopify?.designMode || false;
  }

  get isB2BCustomer() {
    return this.config.isB2BCustomer || false;
  }

  get isLoggedIn() {
    return this.config.isLoggedIn || false;
  }

  get loginUrl() {
    return this.config.loginUrl || '/account/login';
  }

  get currentContext() {
    return document.body.classList.contains('is-b2b') ? 'b2b' : 'default';
  }

  get hasStoredB2BContext() {
    return localStorage.getItem(B2BContextSwitcher.STORAGE_KEY) !== null;
  }

  /**
   * Initialize context on page load
   */
  initializeContext() {
    // In design mode, restore stored preference for editing
    if (this.isDesignMode) {
      if (this.hasStoredB2BContext) {
        this.applyContext('b2b', false);
      }
      return;
    }

    // On frontend, B2B context is auto-applied by silent init based on customer.b2b?
    // This method just ensures UI state matches
    if (this.isB2BCustomer) {
      this.applyContext('b2b', false);
    }
  }

  bindEvents() {
    // Toggle dropdown - click
    this.toggle?.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleDropdown();
    });

    // Toggle dropdown - keyboard (Enter/Space)
    this.toggle?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleDropdown();
      }
    });

    // Option selection - click
    this.options?.forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        const context = option.dataset.context;
        this.setContext(context);
        this.closeDropdown();
      });

      // Option selection - keyboard (Enter/Space)
      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const context = option.dataset.context;
          this.setContext(context);
          this.closeDropdown();
        }
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.closeDropdown();
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeDropdown();
      }
    });
  }

  toggleDropdown() {
    const isExpanded = this.toggle.getAttribute('aria-expanded') === 'true';
    this.toggle.setAttribute('aria-expanded', !isExpanded);
    this.classList.toggle('is-open', !isExpanded);
  }

  closeDropdown() {
    this.toggle?.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
  }

  /**
   * Set the B2B context
   * @param {string} context - 'b2b' or 'default'
   */
  setContext(context) {
    const enableB2B = context === 'b2b';

    // In design mode, always allow switching
    if (this.isDesignMode) {
      this.applyContext(context, true);
      return;
    }

    // Switching to B2B context
    if (enableB2B) {
      // Not logged in - redirect to login
      if (!this.isLoggedIn) {
        localStorage.setItem(B2BContextSwitcher.STORAGE_KEY, '1');
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `${this.loginUrl}?return_url=${returnUrl}`;
        return;
      }

      // Logged in but not a B2B customer
      if (!this.isB2BCustomer) {
        this.showNotB2BMessage();
        return;
      }
    }

    // Switching FROM B2B to Consumer view
    // B2B pricing is server-side, so we need to log them out to show consumer prices
    if (!enableB2B && this.isB2BCustomer && this.isLoggedIn) {
      localStorage.removeItem(B2BContextSwitcher.STORAGE_KEY);
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/account/logout?return_url=${returnUrl}`;
      return;
    }

    this.applyContext(context, true);
  }

  /**
   * Apply context to the page
   * @param {string} context - 'b2b' or 'default'
   * @param {boolean} persist - Whether to save to localStorage
   */
  applyContext(context, persist = true) {
    const enableB2B = context === 'b2b';
    
    document.body.classList.toggle('is-b2b', enableB2B);
    
    // Update active option styling
    this.options?.forEach(option => {
      option.classList.toggle('is-active', option.dataset.context === context);
    });

    // In design mode, swap color scheme classes on elements with b2b-color-* class
    if (this.isDesignMode) {
      this.swapColorSchemes(enableB2B);
    }

    // Handle B2B priority accordions (open/close based on context)
    this.handleB2BPriorityAccordions(enableB2B);

    // Persist preference (key exists = B2B, key removed = consumer)
    if (persist) {
      if (enableB2B) {
        localStorage.setItem(B2BContextSwitcher.STORAGE_KEY, '1');
      } else {
        localStorage.removeItem(B2BContextSwitcher.STORAGE_KEY);
      }
    }

    // Dispatch custom event for other components to react
    document.dispatchEvent(new CustomEvent('b2b:context-changed', {
      detail: { context, isB2B: enableB2B }
    }));
  }

  /**
   * Handle B2B priority accordions - click to open when in B2B context
   * Processes all b2b-priority accordions (desktop and mobile duplicates)
   * @param {boolean} enableB2B - Whether B2B context is active
   */
  handleB2BPriorityAccordions(enableB2B) {
    const priorityBlocks = document.querySelectorAll('.product__block--collapsible_tab.b2b-priority');
    
    priorityBlocks.forEach(block => {
      const summary = block.querySelector('details > summary');
      if (!summary) return;

      if (enableB2B) {
        // Click to open - this triggers proper icon changes and other accordion behavior
        const details = summary.closest('details');
        if (details && !details.hasAttribute('open')) {
          summary.click();
        }
      } else {
        // Close if it was opened by us
        // Note: we check !data-default-open to preserve ones that should always be open
        const details = summary.closest('details');
        if (details && details.hasAttribute('open') && !details.hasAttribute('data-default-open')) {
          summary.click();
        }
      }
    });
  }


  /**
   * Swap color scheme classes in design mode
   * Elements with both color-* and b2b-color-* classes will have their active scheme swapped
   * @param {boolean} enableB2B - Whether to use B2B color scheme
   */
  swapColorSchemes(enableB2B) {
    // Find all elements with b2b-color-* class
    const elements = document.querySelectorAll('[class*="b2b-color-"]');
    
    elements.forEach(el => {
      if (enableB2B) {
        // Switching TO B2B mode
        const classes = el.className.split(' ');
        let defaultScheme = null;
        let b2bSchemeClass = null;
        
        // Find the current color scheme and b2b color scheme
        classes.forEach(cls => {
          // Match color-* but not b2b-color-*
          if (cls.startsWith('color-') && !cls.startsWith('b2b-color-')) {
            defaultScheme = cls;
          }
          if (cls.startsWith('b2b-color-')) {
            b2bSchemeClass = cls;
          }
        });
        
        // Convert b2b-color-* to color-* format
        const b2bScheme = b2bSchemeClass ? b2bSchemeClass.replace('b2b-', '') : null;
        
        if (defaultScheme && b2bScheme && defaultScheme !== b2bScheme) {
          // Store original scheme BEFORE swapping
          el.dataset.defaultColorScheme = defaultScheme;
          // Swap to B2B scheme
          el.classList.remove(defaultScheme);
          el.classList.add(b2bScheme);
        }
      } else {
        // Switching BACK to default mode
        const storedDefault = el.dataset.defaultColorScheme;
        if (storedDefault) {
          // Find and remove the current active color-* class (which is the B2B one)
          const classes = el.className.split(' ');
          classes.forEach(cls => {
            // Remove any color-* class that isn't b2b-color-* and isn't the stored default
            if (cls.startsWith('color-') && !cls.startsWith('b2b-color-')) {
              el.classList.remove(cls);
            }
          });
          // Add back the stored default
          el.classList.add(storedDefault);
          // Clean up the data attribute
          delete el.dataset.defaultColorScheme;
        }
      }
    });
  }

  /**
   * Show message when user is not a B2B customer
   */
  showNotB2BMessage() {
    console.info('[B2B Context] User is not a B2B customer');
    
    if (!this.isDesignMode) {
      document.dispatchEvent(new CustomEvent('b2b:access-denied', {
        detail: { 
          message: 'Business account access required. Please contact us to apply for a trade account.'
        }
      }));
    }
  }
}

// Register custom element
if (!customElements.get('b2b-context-switcher')) {
  customElements.define('b2b-context-switcher', B2BContextSwitcher);
}

// Export for module usage
window.B2BContextSwitcher = B2BContextSwitcher;

/**
 * B2B Context Initialization
 * 
 * Runs on every page load to ensure B2B context is correctly set
 * based on customer.b2b? from Shopify.
 * - If customer is B2B, apply is-b2b class and set localStorage
 * - If customer is not B2B, clear localStorage and remove class
 */
(function initB2BContext() {
  const STORAGE_KEY = 'jd_b2b_context';
  const config = window.FoxTheme?.b2b || {};
  const isDesignMode = config.isDesignMode || (typeof Shopify !== 'undefined' && Shopify.designMode);
  const isB2BCustomer = config.isB2BCustomer || false;
  const hasStoredContext = localStorage.getItem(STORAGE_KEY) !== null;

  // In design mode, don't auto-set - let the switcher handle it
  if (isDesignMode) {
    return;
  }

  // If customer is B2B, automatically enable B2B context
  if (isB2BCustomer) {
    document.body.classList.add('is-b2b');
    if (!hasStoredContext) {
      localStorage.setItem(STORAGE_KEY, '1');
    }
    return;
  }

  // If not a B2B customer, ensure consumer mode
  if (hasStoredContext) {
    localStorage.removeItem(STORAGE_KEY);
  }
  document.body.classList.remove('is-b2b');
})();
